import React, { useEffect, useState } from 'react';
import Axios, { AxiosError } from 'axios';
import moment from 'moment-timezone';
import * as Routes from '@/modules/routes';
import { Button, UnstyledButton } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import Pill from '@/components/ui/Pill';
import { GradientSpinner } from '@/components/ui/Spinner';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import { CsvValidationError } from '@/components/common/Validations/CsvValidations';
import { errorsCsvDataUri } from '@/components/common/Validations/errorsCsv';
import rosteringCardStyles from '../RosteringCard.module.scss';
import completedCardStyles from './CompletedCard.module.scss';
import {
  ExpectedFile,
  FileAnnotation,
  fileAnnotation,
  rosterImportType,
  runRosterValidations,
  sniffImportType,
  syncWillConsume,
} from './SftpFiles.utils';

interface SftpFileData {
  name: string;
  uploaded_at: string;
}

interface SftpDirectoryData {
  expected_files: ExpectedFile[];
  files: SftpFileData[];
  import_path: string;
  next_sync_at: string;
  program_codes?: string[];
  run_now_expected_files: ExpectedFile[];
  strict_password_policies?: string[];
}

export interface CsvSummary {
  headers?: string[];
  missing_headers?: string[];
  parse_error?: string;
  program_codes?: { code: string; count: number }[];
  program_codes_omitted?: number;
  row_count?: number;
}

interface SftpFilesProps {
  subscriberId: number;
}

const TIMESTAMP_FORMAT = `MMM DD, YYYY [at] hh:mm A [${moment.tz(moment.tz.guess()).zoneAbbr()}]`;
const UPLOADED_FORMAT = `MM/DD/YYYY h:mm A [${moment.tz(moment.tz.guess()).zoneAbbr()}]`;

const ExpectedFileItem = ({ file }: { file: ExpectedFile }) => (
  <li className="tw-flex tw-items-center tw-gap-1">
    <Icon
      fill={file.present ? COLORS.green : COLORS.red}
      height="12"
      icon={file.present ? 'Check' : 'Close'}
      width="12"
    />
    <code>{file.name}</code>
    <Text
      className={file.present ? 'tw-text-dark-green' : 'tw-text-red'}
      size="sm"
      tag="span"
    >
      {file.present ? 'found' : 'missing'}
    </Text>
  </li>
);

const SyncVerdict = ({ expectedFiles }: { expectedFiles: ExpectedFile[] }) => {
  const found = expectedFiles.filter(file => file.present);

  if (found.length === expectedFiles.length) {
    return (
      <Text className="tw-text-dark-green tw-flex tw-items-center tw-gap-1" size="sm">
        <Icon fill={COLORS.green} height="12" icon="Check" width="12" />
        Both files found — the next sync will run a full sync.
      </Text>
    );
  }

  if (found.length === 0) {
    return (
      <Text className="tw-text-red tw-flex tw-items-center tw-gap-1" size="sm">
        <Icon fill={COLORS.red} height="12" icon="Close" width="12" />
        No matching files — the next sync will silently skip (no roster event, no error).
      </Text>
    );
  }

  const foundKind = found[0].name.replace(/_\d{8}\.csv$/i, '');

  return (
    <Text className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1" size="sm">
      <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
      {`Only the ${foundKind} file was found — the next sync will run a partial update (${foundKind} only).`}
    </Text>
  );
};

const FileSummary = ({ hasError, summary }: { hasError: boolean; summary: CsvSummary | null }) => {
  if (hasError) return <Text className="tw-text-red" size="sm">Could not load the file summary.</Text>;
  if (!summary) return <GradientSpinner size={14} />;
  if (summary.parse_error) {
    return (
      <Text className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1" size="sm">
        <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
        {summary.parse_error}
      </Text>
    );
  }

  const missingHeaders = summary.missing_headers ?? [];
  const programCodes = summary.program_codes ?? [];
  const omittedCount = summary.program_codes_omitted ?? 0;

  return (
    <div className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-4 tw-gap-y-1">
      <Text className="tw-text-very-dark-grey" size="sm" tag="span">Rows</Text>
      <Text size="sm" tag="span">{(summary.row_count ?? 0).toLocaleString()}</Text>

      <Text className="tw-text-very-dark-grey" size="sm" tag="span">Columns</Text>
      <Text size="sm" tag="span"><code>{(summary.headers ?? []).join(', ')}</code></Text>

      {missingHeaders.length > 0 && (
        <Text className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1 tw-col-span-2" size="sm">
          <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
          Missing required columns: <code>{missingHeaders.join(', ')}</code>
        </Text>
      )}

      {programCodes.length > 0 && (
        <>
          <Text className="tw-text-very-dark-grey" size="sm" tag="span">Program codes</Text>
          <div>
            <div className="tw-grid tw-grid-cols-[auto_auto] tw-gap-x-4 tw-w-fit">
              {programCodes.map(pc => (
                <React.Fragment key={pc.code}>
                  <Text size="sm" tag="span"><code>{pc.code}</code></Text>
                  <Text className="tw-text-right" size="sm" tag="span">{pc.count.toLocaleString()}</Text>
                </React.Fragment>
              ))}
            </div>
            {omittedCount > 0 && (
              <Text className="tw-text-very-dark-grey" size="sm">and {omittedCount} more</Text>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const RunNowExpectations = ({ directory }: { directory: SftpDirectoryData }) => {
  const runNowFiles = directory.run_now_expected_files ?? [];
  const differs = runNowFiles.some((file, index) => file.name !== directory.expected_files[index]?.name);

  if (runNowFiles.length === 0 || !differs) return null;

  return (
    <div className="tw-mt-2">
      <Text size="sm">Clicking Run Sync right now would instead look for:</Text>
      <ul className="tw-list-none tw-p-0 tw-my-1">
        {runNowFiles.map(file => <ExpectedFileItem key={file.name} file={file} />)}
      </ul>
    </div>
  );
};

const ERROR_TYPE_ORDER = ['Incorrect header row', 'Invalid row', 'Duplicate rows',
  'Invalid username', 'Invalid password', 'Invalid program code', 'Missing information'];

const ValidationResults = ({ errors }: { errors: CsvValidationError[] }) => {
  if (errors.length === 0) {
    return (
      <Text className="tw-text-dark-green tw-flex tw-items-center tw-gap-1" size="sm">
        <Icon fill={COLORS.green} height="12" icon="Check" width="12" />
        No validation errors found.
      </Text>
    );
  }

  const types = ERROR_TYPE_ORDER.concat(
    errors.map(error => error.error).filter(type => !ERROR_TYPE_ORDER.includes(type)),
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      {types.map((type) => {
        const ofType = errors.filter(error => error.error === type);
        if (ofType.length === 0) return null;

        const rowNumbers = Array.from(new Set(ofType.map(error => error.row)));
        const rowsText = rowNumbers.slice(0, 5).join(', ') + (rowNumbers.length > 5 ? ', …' : '');

        return (
          <Text key={type} className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1" size="sm">
            <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
            {`${type} (${ofType.length}) — rows ${rowsText}`}
          </Text>
        );
      })}
      <a download="tci_upload_errors.csv" href={errorsCsvDataUri(errors)}>Export errors</a>
    </div>
  );
};

const ValidationBadge = ({ errors }: { errors: CsvValidationError[] }) => {
  if (errors.length === 0) {
    return (
      <Text className="tw-text-dark-green tw-flex tw-items-center tw-gap-1" size="sm">
        <Icon fill={COLORS.green} height="12" icon="Check" width="12" />
        Passes validations
      </Text>
    );
  }

  return (
    <span className="tw-flex tw-items-center tw-gap-2">
      <Text className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1" size="sm" tag="span">
        <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
        {`${errors.length} validation error${errors.length === 1 ? '' : 's'}`}
      </Text>
      <a download="tci_upload_errors.csv" href={errorsCsvDataUri(errors)}>Export errors</a>
    </span>
  );
};

const AnnotationNote = ({ annotation }: { annotation: FileAnnotation }) => {
  if (annotation.level === 'muted') {
    return <Text className="tw-text-very-dark-grey" size="sm">{annotation.message}</Text>;
  }

  return (
    <Text className="tw-text-dark-orange tw-flex tw-items-center tw-gap-1" size="sm">
      <Icon fill={COLORS['dark-orange']} height="12" icon="Warning" width="12" />
      {annotation.message}
    </Text>
  );
};

// the content endpoint's error bodies arrive as raw text because of the transformResponse override
const serverError = (error: AxiosError<string>): string | null => {
  if (!error.response?.data) return null;

  try {
    return JSON.parse(error.response.data).error ?? null;
  } catch {
    return null;
  }
};

const FileRow = ({ directory, file, subscriberId }: {
  directory: SftpDirectoryData;
  file: SftpFileData;
  subscriberId: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState<CsvSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [validationErrors, setValidationErrors] = useState<CsvValidationError[] | null>(null);
  const [validationFailure, setValidationFailure] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const annotation = fileAnnotation(file.name, directory.expected_files);
  const downloadable = !file.name.includes('/');
  const importType = rosterImportType(file.name);
  const inspectable = /\.csv$/i.test(file.name) && !file.name.includes('/');

  // a current or future sync would consume this file, so validate it without waiting for a click
  const autoValidate = syncWillConsume(file.name, directory.run_now_expected_files ?? []);

  const runValidations = () => {
    setIsValidating(true);
    setValidationFailure(null);
    Axios
      .get<string>(Routes.plato_api_content_subscriber_sftp_files_path(subscriberId), {
      params: { name: file.name },
      responseType: 'text',
      transformResponse: [(data: string) => data],
    })
      .then((response) => {
        setValidationErrors(runRosterValidations(
          response.data,
          importType ?? sniffImportType(response.data),
          directory.program_codes ?? [],
          directory.strict_password_policies ?? [],
        ));
      })
      .catch((error: AxiosError<string>) => {
        setValidationFailure(serverError(error) ?? 'Could not load the file for validation.');
      })
      .finally(() => setIsValidating(false));
  };

  useEffect(() => {
    if (autoValidate) runValidations();
  }, [autoValidate]);

  const toggle = () => {
    const opening = !expanded;
    setExpanded(opening);
    if (opening && !summary) {
      setSummaryError(false);
      Axios
        .get<{ data: CsvSummary }>(
        Routes.plato_api_summary_subscriber_sftp_files_path(subscriberId),
        { params: { name: file.name } },
      )
        .then(response => setSummary(response.data.data))
        .catch(() => setSummaryError(true));
    }
  };

  return (
    <li className="tw-px-3 tw-py-2">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-flex tw-items-center tw-gap-1">
          {inspectable && (
            <UnstyledButton aria-expanded={expanded} aria-label={`Inspect ${file.name}`} onClick={toggle}>
              <Icon height="10" icon={expanded ? 'ChevronDown' : 'ChevronRight'} width="10" />
            </UnstyledButton>
          )}
          <code>{file.name}</code>
        </span>
        <span className="tw-flex tw-items-center tw-gap-2">
          <Text className="tw-text-very-dark-grey tw-whitespace-nowrap" size="sm" tag="span">
            {moment(file.uploaded_at).format(UPLOADED_FORMAT)}
          </Text>
          {downloadable && (
            <a
              aria-label={`Download ${file.name}`}
              className="tw-flex tw-items-center"
              href={Routes.plato_api_download_subscriber_sftp_files_path(subscriberId, { name: file.name })}
            >
              <Icon fill={COLORS['tci-purple']} height="14" icon="Download" width="14" />
            </a>
          )}
        </span>
      </div>
      {annotation && <AnnotationNote annotation={annotation} />}
      {inspectable && validationErrors === null && (
        <span className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
          <Button
            outlineStyle
            small
            label={`Run validations for ${file.name}`}
            onClick={() => !isValidating && runValidations()}
          >
            {isValidating ? 'Validating…' : 'Run validations'}
          </Button>
          {validationFailure && !isValidating && (
            <Text className="tw-text-red" size="sm" tag="span">{validationFailure}</Text>
          )}
        </span>
      )}
      {validationErrors !== null && !expanded && <ValidationBadge errors={validationErrors} />}
      {expanded && (
        <div className="tw-mt-1 tw-pl-4">
          <FileSummary hasError={summaryError} summary={summary} />
          {validationErrors !== null && (
            <div
              className={'tw-mt-2 tw-pt-2 tw-border-t tw-border-x-0 tw-border-b-0 tw-border-solid '
                + 'tw-border-light-grey'}
            >
              <ValidationResults errors={validationErrors} />
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const FileList = ({ directory, subscriberId }: { directory: SftpDirectoryData; subscriberId: number }) => (
  <ul
    className={'tw-list-none tw-p-0 tw-mt-2 tw-mb-0 tw-border-solid tw-border-[1px] tw-border-med-grey '
      + 'tw-rounded-md tw-divide-y tw-divide-solid tw-divide-med-grey'}
  >
    {/* uploaded_at in the key remounts a row when a same-named file is re-uploaded, so
        Refresh resets stale summary and validation state */}
    {directory.files.map(file => (
      <FileRow key={`${file.name}-${file.uploaded_at}`} directory={directory} file={file} subscriberId={subscriberId} />
    ))}
  </ul>
);

const SftpFiles = ({ subscriberId }: SftpFilesProps) => {
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [directory, setDirectory] = useState<SftpDirectoryData | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    Axios
      .get<{ data: SftpDirectoryData }>(Routes.plato_api_subscriber_sftp_files_path(subscriberId))
      .then((response) => {
        setDirectory(response.data.data);
        setHasError(false);
      })
      .catch(() => setHasError(true))
      .finally(() => {
        setCheckedAt(new Date());
        setIsLoading(false);
      });
  }, [refreshCount, subscriberId]);

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-flex tw-items-center tw-gap-2">
          <h2 className={`${completedCardStyles.header} tw-my-0`}>SFTP Files</h2>
          <Pill pillClassName="tw-py-0" pillColor="purple">
            <Text size="sm" tag="span">TCI staff only</Text>
          </Pill>
        </span>
        <span className="tw-flex tw-items-center tw-gap-2">
          {checkedAt && (
            <Text className="tw-text-very-dark-grey" size="sm" tag="span">
              Checked at {moment(checkedAt).format('h:mm:ss A')}
            </Text>
          )}
          <Button
            outlineStyle
            small
            onClick={() => !isLoading && setRefreshCount(count => count + 1)}
          >
            {isLoading ? 'Checking…' : 'Refresh'}
          </Button>
        </span>
      </div>
      <Text className="tw-text-very-dark-grey" size="sm">TCI CSV rostering imports</Text>
      <hr className={rosteringCardStyles.hr} />
      <div>
        {directory && (
          <Text className="tw-text-very-dark-grey" size="sm">
            Folder: <code>{directory.import_path}</code>
          </Text>
        )}
        {hasError && (
          <Text className="tw-text-red" size="sm">Could not load the SFTP folder contents.</Text>
        )}
        {!hasError && !directory && <GradientSpinner />}
        {!hasError && directory && (
          <>
            <Text size="sm">
              Next sync on
              {' '}
              {moment(directory.next_sync_at).format(TIMESTAMP_FORMAT)}
              {' '}
              will look for:
            </Text>
            <ul className="tw-list-none tw-p-0 tw-my-1">
              {directory.expected_files.map(file => <ExpectedFileItem key={file.name} file={file} />)}
            </ul>
            <SyncVerdict expectedFiles={directory.expected_files} />
            <RunNowExpectations directory={directory} />
            {directory.files.length === 0 && (
              <Text className="tw-text-very-dark-grey tw-mt-2" size="sm">The SFTP folder is empty.</Text>
            )}
            {directory.files.length > 0 && <FileList directory={directory} subscriberId={subscriberId} />}
            <Text className="tw-text-very-dark-grey tw-mt-1" size="sm">
              Files older than 1 week are deleted from this folder automatically.
            </Text>
          </>
        )}
      </div>
    </>
  );
};

export default SftpFiles;
